import CalendarToday from '@mui/icons-material/CalendarToday'
import ChevronLeft from '@mui/icons-material/ChevronLeft'
import ChevronRight from '@mui/icons-material/ChevronRight'
import Close from '@mui/icons-material/Close'
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Typography,
  CircularProgress,
} from '@mui/material'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import dayjs from 'dayjs'
import { useRef, useState, useEffect, useCallback } from 'react'
import { DateSelectorCard } from 'src/mentor/components/PrescriptionMakingPage/screeningReportViewer/components/styledComponents'

// Custom hook for infinite date generation
const useInfiniteDateRange = (startDate = dayjs(), initialDays = 30, minDate = null, selectedDate = null, maxDate = null) => {
  const [dates, setDates] = useState(() => {
    const initialDates = []
    
    // Determine the actual start date for generation
    let actualStartDate = startDate
    
    if (selectedDate && dayjs.isDayjs(selectedDate)) {
      // If selectedDate is provided, ensure it's in the range
      // Start from a few days before selectedDate, but not before minDate
      const daysBeforeSelected = Math.max(3, Math.floor(initialDays / 4))
      let candidateStart = selectedDate.subtract(daysBeforeSelected, 'day')
      
      // Don't start before minDate if provided
      if (minDate && dayjs.isDayjs(minDate) && candidateStart.isBefore(minDate, 'day')) {
        candidateStart = minDate
      }
      
      actualStartDate = candidateStart
    } else if (minDate && dayjs.isDayjs(minDate)) {
      actualStartDate = minDate
    }
    
    // Generate initial dates
    const daysToGenerate = selectedDate && dayjs.isDayjs(selectedDate) && selectedDate.isAfter(actualStartDate.add(initialDays - 1, 'day'), 'day')
      ? selectedDate.diff(actualStartDate, 'day') + 1
      : initialDays
    
    for (let i = 0; i < daysToGenerate; i++) {
      const date = actualStartDate.add(i, 'day')
      // Check maxDate if provided
      if (maxDate && dayjs.isDayjs(maxDate) && date.isAfter(maxDate, 'day')) {
        break
      }
      initialDates.push(date)
    }
    
    return initialDates
  })

  const loadMoreDates = useCallback(() => {
    if (dates.length === 0) return
    
    const lastDate = dates[dates.length - 1]
    const maxDateDayjs = maxDate ? (dayjs.isDayjs(maxDate) ? maxDate : dayjs(maxDate)) : null
    
    // Check if we've reached maxDate
    if (maxDateDayjs && (lastDate.isSame(maxDateDayjs, 'day') || lastDate.isAfter(maxDateDayjs, 'day'))) {
      return // Already at max date
    }
    
    // Load next batch of dates (30 days at a time)
    const newDates = []
    const batchSize = 30
    
    for (let i = 1; i <= batchSize; i++) {
      const nextDate = lastDate.add(i, 'day')
      
      // Stop if we exceed maxDate
      if (maxDateDayjs && nextDate.isAfter(maxDateDayjs, 'day')) {
        break
      }
      
      newDates.push(nextDate)
    }
    
    if (newDates.length > 0) {
      setDates(prevDates => [...prevDates, ...newDates])
    }
  }, [dates, maxDate])

  return { dates, loadMoreDates }
}

// Custom hook for scroll behavior
const useScrollNavigation = () => {
  const scrollContainerRef = useRef(null)

  const scrollLeft = () => {
    scrollContainerRef.current?.scrollBy({
      left: -200,
      behavior: 'smooth',
    })
  }

  const scrollRight = () => {
    scrollContainerRef.current?.scrollBy({
      left: 200,
      behavior: 'smooth',
    })
  }

  return { scrollContainerRef, scrollLeft, scrollRight }
}

// Header component
const DateSelectorHeader = ({ onCalendarClick }) => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      mb: 2,
    }}
  >
    <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#333' }}>
      Date and Time
    </Typography>
    <IconButton
      onClick={onCalendarClick}
      sx={{
        color: '#666',
        '&:hover': { backgroundColor: '#f5f5f5' },
      }}
    >
      <CalendarToday />
    </IconButton>
  </Box>
)

// Navigation arrow component
const NavigationArrow = ({ direction, onClick }) => (
  <IconButton
    onClick={onClick}
    sx={{
      minWidth: 40,
      height: 40,
      backgroundColor: '#f5f5f5',
      '&:hover': { backgroundColor: '#e0e0e0' },
    }}
  >
    {direction === 'left' ? <ChevronLeft /> : <ChevronRight />}
  </IconButton>
)

// Date card component
const DateCard = ({ date, isSelected, onClick, bookingDate }) => {
  // Calculate days difference from booking date
  const getDaysDifference = () => {
    if (!bookingDate) return null
    
    const bookingDateDayjs = dayjs.isDayjs(bookingDate) ? bookingDate : dayjs(bookingDate)
    const dateDayjs = dayjs.isDayjs(date) ? date : dayjs(date)
    
    // Start of day for accurate day difference calculation
    const bookingStart = bookingDateDayjs.startOf('day')
    const dateStart = dateDayjs.startOf('day')
    
    const diffDays = dateStart.diff(bookingStart, 'day')
    
    if (diffDays === 0) {
      return 'Today'
    } else if (diffDays === 1) {
      return 'In 1 day'
    } else if (diffDays > 1) {
      return `In ${diffDays} days`
    } else if (diffDays === -1) {
      return '1 day ago'
    } else {
      return `${Math.abs(diffDays)} days ago`
    }
  }
  
  const daysText = getDaysDifference()
  
  return (
    <DateSelectorCard onClick={() => onClick(date)} isSelected={isSelected}>
      <Typography
        variant="caption"
        sx={{
          display: 'block',
          fontWeight: 'bold',
          fontSize: '12px',
          textTransform: 'uppercase',
          opacity: isSelected ? 1 : 0.7,
        }}
      >
        {date.format('ddd')}
      </Typography>
      <Typography
        variant="h6"
        sx={{
          fontWeight: 'bold',
          my: 0.5,
          fontSize: '18px',
        }}
      >
        {date.format('DD')} {date.format('MMM')}
      </Typography>
      {daysText && (
        <Typography
          variant="caption"
          sx={{
            display: 'block',
            fontSize: '10px',
            opacity: isSelected ? 0.8 : 0.6,
            mt: 0.25,
            fontWeight: 500,
          }}
        >
          ({daysText})
        </Typography>
      )}
    </DateSelectorCard>
  )
}

// Scrollable date list component
const ScrollableDateList = ({
  dates,
  selectedDate,
  onDateClick,
  scrollRef,
  getSlotCount,
  minDate,
  maxDate,
  bookingDate,
  onLoadMore,
  isLoadingMore = false,
}) => {
  // Filter dates based on minDate and maxDate
  // Normalize minDate and maxDate to dayjs for comparison
  const minDateNormalized = minDate ? (dayjs.isDayjs(minDate) ? minDate : dayjs(minDate)) : null
  const maxDateNormalized = maxDate ? (dayjs.isDayjs(maxDate) ? maxDate : dayjs(maxDate)) : null
  
  const filteredDates = dates?.filter((date) => {
    // Ensure date is a dayjs object
    const dateNormalized = dayjs.isDayjs(date) ? date : dayjs(date)
    
    // Exclude dates before minDate (dates equal to or after minDate are OK)
    if (minDateNormalized && dateNormalized.isBefore(minDateNormalized, 'day')) {
      return false
    }
    // Exclude dates after maxDate (dates equal to or before maxDate are OK)
    if (maxDateNormalized && dateNormalized.isAfter(maxDateNormalized, 'day')) {
      return false
    }
    return true
  })

  // Auto-scroll to selected date when it changes
  useEffect(() => {
    if (selectedDate && scrollRef.current && filteredDates?.length > 0) {
      const selectedIndex = filteredDates.findIndex((date) => 
        date.isSame(selectedDate, 'day')
      )
      if (selectedIndex >= 0) {
        const cardElement = scrollRef.current.children[selectedIndex]
        if (cardElement) {
          cardElement.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' })
        }
      }
    }
  }, [selectedDate, filteredDates, scrollRef])

  // Infinite scroll detection
  useEffect(() => {
    const container = scrollRef.current
    if (!container || !onLoadMore) return

    const handleScroll = () => {
      const { scrollLeft, scrollWidth, clientWidth } = container
      // Load more when user scrolls to within 200px of the end
      const threshold = 200
      const isNearEnd = scrollLeft + clientWidth >= scrollWidth - threshold

      if (isNearEnd && !isLoadingMore) {
        onLoadMore()
      }
    }

    container.addEventListener('scroll', handleScroll, { passive: true })
    return () => {
      container.removeEventListener('scroll', handleScroll)
    }
  }, [scrollRef, onLoadMore, isLoadingMore])

  return (
    <Box
      ref={scrollRef}
      sx={{
        display: 'flex',
        gap: 1,
        overflowX: 'auto',
        scrollBehavior: 'smooth',
        padding: '8px 0px',
        flex: 1,
        '&::-webkit-scrollbar': { display: 'none' },
        scrollbarWidth: 'none',
        msOverflowStyle: 'none',
      }}
    >
      {filteredDates?.map((date) => {
        return (
          <DateCard
            key={date.format('YYYY-MM-DD')}
            date={date}
            isSelected={selectedDate && date.isSame(selectedDate, 'day')}
            onClick={onDateClick}
            bookingDate={bookingDate}
          />
        )
      })}
      {isLoadingMore && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minWidth: '100px',
            padding: '16px',
          }}
        >
          <CircularProgress size={20} />
        </Box>
      )}
    </Box>
  )
}

// Calendar dialog component
const CalendarDialog = ({ open, onClose, selectedDate, onDateChange, minDate, maxDate }) => (
  <Dialog open={open} onClose={onClose} maxWidth="sm">
    <DialogTitle sx={{ borderBottom: '1px solid #e0e0e0' }}>
      <div className="d-flex justify-content-between align-items-center">
        <Typography variant="h6">Select Date</Typography>
        <IconButton onClick={onClose}>
          <Close />
        </IconButton>
      </div>
    </DialogTitle>
    <DialogContent sx={{ pb: 3 }}>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <DateCalendar
          value={selectedDate}
          onChange={onDateChange}
          sx={{ width: '100%', mt: 2 }}
          minDate={minDate || dayjs()}
          maxDate={maxDate}
        />
      </LocalizationProvider>
    </DialogContent>
  </Dialog>
)

// Main component
export default function SidewaysDateSelector({
  selectedDate,
  setSelectedDate,
  daysToShow = 14,
  minDate,
  maxDate,
  bookingDate = null, // The report date to calculate days from
  onDatesLoaded = null, // Callback when new dates are loaded (for lazy loading slots)
}) {
  const [calendarOpen, setCalendarOpen] = useState(false)
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  
  // Convert minDate and maxDate to dayjs if they're Date objects
  const minDateDayjs = minDate ? (dayjs.isDayjs(minDate) ? minDate : dayjs(minDate)) : null
  const maxDateDayjs = maxDate ? (dayjs.isDayjs(maxDate) ? maxDate : dayjs(maxDate)) : null
  const selectedDateDayjs = selectedDate ? (dayjs.isDayjs(selectedDate) ? selectedDate : dayjs(selectedDate)) : null
  
  // Generate dates starting from appropriate date
  const startDateForGeneration = selectedDateDayjs || minDateDayjs || dayjs()
  const { dates, loadMoreDates } = useInfiniteDateRange(startDateForGeneration, daysToShow, minDateDayjs, selectedDateDayjs, maxDateDayjs)
  
  const { scrollContainerRef, scrollLeft, scrollRight } = useScrollNavigation()

  // Handle loading more dates
  const handleLoadMore = useCallback(async () => {
    if (isLoadingMore) return
    
    setIsLoadingMore(true)
    
    // Get the current last date before loading more
    const lastDateBeforeLoad = dates[dates.length - 1]
    
    // Load more dates
    loadMoreDates()
    
    // Wait a bit for state to update, then trigger lazy loading of slots
    setTimeout(() => {
      if (onDatesLoaded && lastDateBeforeLoad) {
        // Calculate the range of new dates that were just loaded
        const newDatesStart = lastDateBeforeLoad.add(1, 'day')
        const newDatesEnd = newDatesStart.add(30, 'days') // Assuming 30 days per batch
        
        // Notify parent to load slots for new dates in background
        onDatesLoaded(newDatesStart, newDatesEnd)
      }
      setIsLoadingMore(false)
    }, 100)
  }, [dates, loadMoreDates, isLoadingMore, onDatesLoaded])

  const handleDateClick = (date) => {
    if (setSelectedDate) {
      setSelectedDate(date)
    }
  }

  const handleCalendarDateChange = (newDate) => {
    if (newDate) {
      // Ensure the date is within bounds
      let finalDate = newDate
      if (minDateDayjs && finalDate.isBefore(minDateDayjs, 'day')) {
        finalDate = minDateDayjs
      }
      if (maxDateDayjs && finalDate.isAfter(maxDateDayjs, 'day')) {
        finalDate = maxDateDayjs
      }
      setSelectedDate(finalDate)
      setCalendarOpen(false)
    }
  }

  const openCalendar = () => setCalendarOpen(true)
  const closeCalendar = () => setCalendarOpen(false)

  return (
    <Box>
      <DateSelectorHeader onCalendarClick={openCalendar} />

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          position: 'relative',
        }}
      >
        <NavigationArrow direction="left" onClick={scrollLeft} />

        <ScrollableDateList
          dates={dates}
          selectedDate={selectedDateDayjs}
          minDate={minDateDayjs}
          maxDate={maxDateDayjs}
          onDateClick={handleDateClick}
          scrollRef={scrollContainerRef}
          bookingDate={bookingDate}
          onLoadMore={handleLoadMore}
          isLoadingMore={isLoadingMore}
        />

        <NavigationArrow direction="right" onClick={scrollRight} />
      </Box>

      <CalendarDialog
        open={calendarOpen}
        onClose={closeCalendar}
        selectedDate={selectedDateDayjs}
        onDateChange={handleCalendarDateChange}
        minDate={minDateDayjs}
        maxDate={maxDateDayjs}
      />
    </Box>
  )
}
